import React from 'react';
import { QMS_CATEGORY_LABELS } from '../../constants/labels';
import { QMS_CATEGORY_LABELS as TL } from '../../constants/translations/labels';
import PendingChip from './PendingChip';
import TagChip from './TagChip';
import TagInputDropdown from './TagInputDropdown';
import type { TagInputProps } from './types';
import { useTagInput } from './useTagInput';

export type { TagInputProps };
export {
  INPUT_MAX_LENGTH,
  TAG_MAX_COUNT,
  TAG_MAX_LENGTH,
} from './tagValidation';

const TagInput: React.FC<TagInputProps> = ({
  existingTags,
  newTags,
  onChange,
  disabled = false,
  error,
  suggestions = [],
  isSuggestionsLoading = false,
  onSearchChange,
  onPendingChange,
  className,
}) => {
  const {
    inputRef,
    containerRef,
    inputValue,
    inputError,
    showSuggestions,
    dropdownDismissed,
    focusedSuggestionIndex,
    setFocusedSuggestionIndex,
    pendingTags,
    isFocused,
    setIsFocused,
    allTags,
    lastSegment,
    isFloating,
    isTypedOnlyOption,
    showTypedFirst,
    isAddDisabled,
    handleAdd,
    handleToggleSuggestion,
    handleSelectTypedOption,
    handleRemovePending,
    handleInputChange,
    handleKeyDown,
    handleRemove,
  } = useTagInput({
    existingTags,
    newTags,
    onChange,
    disabled,
    suggestions,
    isSuggestionsLoading,
    ...(onSearchChange !== undefined && { onSearchChange }),
    ...(onPendingChange !== undefined && { onPendingChange }),
  });

  const inputId = React.useId();

  return (
    <div
      ref={containerRef}
      className={`flex flex-col gap-1.5 ${className ?? 'w-full'}`}
    >
      {/* Input row */}
      <div className="flex gap-3 items-center">
        {/* Filled input — pending chips + text input rendered inline */}
        <div className="flex-1 relative min-w-0">
          <div
            className="relative flex flex-col bg-[#E5E7E9] rounded-t-[4px] px-4 pt-2 pb-0 transition-colors cursor-text min-h-[58px]"
            style={{
              borderBottom:
                inputError || error
                  ? '2px solid #e11b1b'
                  : isFocused
                    ? '2px solid #2a4455'
                    : 'none',
            }}
            onClick={() => inputRef.current?.focus()}
          >
            <label
              htmlFor={inputId}
              className="absolute left-4 pointer-events-none origin-top-left"
              style={{
                color:
                  inputError || error
                    ? '#E11B1B'
                    : isFocused
                      ? '#2A4455'
                      : '#6a7278',
                top: isFloating ? 6 : 20,
                fontSize: isFloating ? 12 : 16,
                lineHeight: '16px',
                transition:
                  'top 200ms ease-out, font-size 200ms ease-out, color 200ms ease-out',
                zIndex: 1,
              }}
            >
              {QMS_CATEGORY_LABELS.FIELD_TAGS}{' '}
              <span style={{ fontSize: 12 }}>(Opcjonalne)</span>
            </label>

            <div
              className="flex flex-wrap gap-1.5 items-center min-h-[30px]"
              style={{ paddingTop: 24, paddingBottom: 4 }}
            >
              {pendingTags.map((tag) => (
                <React.Fragment key={tag.name}>
                  <PendingChip
                    label={tag.name}
                    onRemove={() => handleRemovePending(tag.name)}
                  />
                  <span className="text-[#474A4C] text-sm select-none leading-5">
                    ,
                  </span>
                </React.Fragment>
              ))}
              <input
                ref={inputRef}
                id={inputId}
                type="text"
                data-testid="tag-input-field"
                value={inputValue}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                disabled={disabled}
                autoComplete="off"
                onFocus={() => setIsFocused(true)}
                onBlur={(e) => {
                  if (
                    !containerRef.current?.contains(e.relatedTarget as Node)
                  ) {
                    setIsFocused(false);
                  }
                }}
                className="bg-transparent outline-none text-[#181A1B] text-sm leading-5 tracking-[0.25px] flex-1 min-w-[80px] placeholder-[#747778] disabled:opacity-50 disabled:cursor-not-allowed"
              />
            </div>

            {!isFocused && !(inputError || error) && (
              <div
                className="absolute bottom-0"
                style={{
                  left: 6,
                  right: 6,
                  height: 1,
                  backgroundColor: '#6a7278',
                }}
              />
            )}

            {(inputError || error) && (
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[#E11B1B] pointer-events-none">
                <svg
                  viewBox="0 0 24 24"
                  fill="currentColor"
                  className="size-5"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <circle cx="12" cy="12" r="10" fill="#e11b1b" />
                  <text
                    x="12"
                    y="16"
                    textAnchor="middle"
                    fill="white"
                    fontSize="14"
                    fontWeight="bold"
                    fontFamily="Arial"
                  >
                    !
                  </text>
                </svg>
              </span>
            )}
          </div>

          <TagInputDropdown
            dropdownDismissed={dropdownDismissed}
            showSuggestions={showSuggestions}
            pendingTags={pendingTags}
            suggestions={suggestions}
            isSuggestionsLoading={isSuggestionsLoading}
            lastSegment={lastSegment}
            focusedSuggestionIndex={focusedSuggestionIndex}
            setFocusedSuggestionIndex={setFocusedSuggestionIndex}
            allTags={allTags}
            isTypedOnlyOption={isTypedOnlyOption}
            showTypedFirst={showTypedFirst}
            handleSelectTypedOption={handleSelectTypedOption}
            handleToggleSuggestion={handleToggleSuggestion}
          />
        </div>

        {/* Add button — enabled when there are pending chips or typed text */}
        <button
          type="button"
          data-testid="tag-add-button"
          onClick={handleAdd}
          disabled={isAddDisabled}
          className={[
            'h-[58px] px-4 rounded-lg border text-sm font-medium leading-5 tracking-[0.1px] transition-colors shrink-0',
            isAddDisabled
              ? 'border-[#DCDDE0] text-[#747778] bg-[#F2F3F5] cursor-not-allowed'
              : 'border-[#DCDDE0] text-[#474A4C] bg-white hover:bg-[#474A4C]/[0.08] cursor-pointer',
          ].join(' ')}
        >
          {QMS_CATEGORY_LABELS.BUTTON_ADD_TAG}
        </button>
      </div>

      {/* Helper row */}
      <div className="flex justify-between items-center px-1">
        <span className="text-xs text-[#474A4C] leading-4">
          {TL.TAG_HELPER.polish}
        </span>
      </div>

      {/* Confirmed tag chips (committed via Add) */}
      {allTags.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-0.5">
          {allTags.map((tag) => (
            <TagChip
              key={tag}
              label={tag}
              onRemove={() => handleRemove(tag)}
              disabled={disabled}
            />
          ))}
        </div>
      )}

      {/* Validation error */}
      {inputError && (
        <p
          data-testid="tag-input-error"
          className="text-[#E11B1B] text-xs leading-4 tracking-[0.4px] mt-0.5"
        >
          {inputError}
        </p>
      )}

      {error && !inputError && (
        <p
          data-testid="tag-input-error"
          className="text-[#E11B1B] text-xs leading-4 tracking-[0.4px] mt-0.5"
        >
          {error}
        </p>
      )}
    </div>
  );
};

export default TagInput;
